import {Box, Card, Container, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Pjdb1ImputEx from "../../../../components/inputs/Pjdb1ImputEx";
import {workAttrList} from "../../types/workForm";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import useMutationHelper from "../../hooks/useMutationHelper";
import {updateWorkClass} from "../../../../utils/awsAmplifyUtil";
import ClassInputGroup, {ClassFormInputs, initClassFormInputs} from "./groups/ClassInputGroup";
import {policies} from "../../policies";
import Forbidden from "../errors/Forbidden";
import useUser from "../../hooks/useUser";
import {WorkClassDocument} from "../../../../types/pj1dbApiWork";

export interface EditClassFormProps {
  work: WorkClassDocument;
  inputs: EditClassFormInputs,
  setInputs: React.Dispatch<React.SetStateAction<EditClassFormInputs>>;
  // eslint-disable-next-line react/require-default-props
  onError?: (err: unknown) => void;
  // eslint-disable-next-line react/require-default-props
  onSuccess?: () => void;
}

export type EditClassFormInputs = ClassFormInputs

export const editClassFormInitInputs: EditClassFormInputs = initClassFormInputs;

export default function EditClassForm(props: EditClassFormProps) {
  const {user, role} = useUser();
  const {
    ConfirmDialog,
    formRef,
    isClear,
    isLoading,
    clear,
    updateClass,
  } = useFormHelper(props);

  if (!policies.editClass.can({user, role, work: props.work})) {
    return <Forbidden/>
  }

  return (
    <>
      <ConfirmDialog
        title='ワーク情報登録'
        message='ワーク情報を登録します。入力内容に間違いがないか確認してください。'
        confirmButton='登録'
        confirmButtonStyle="primary"
      />

      <Container maxWidth="lg">
        <Card sx={{p: 3}}>
          <Grid container spacing={3} alignItems="flex-end" justifyContent='space-between' sx={{mb: 2}}>
            <Grid>
              <Typography variant="h6">対象物登録</Typography>
            </Grid>
            <Grid><Pjdb1ImputEx/></Grid>
          </Grid>

          <form ref={formRef}>
            <ClassInputGroup inputs={props.inputs} setInputs={props.setInputs} isClear={isClear}/>
          </form>
        </Card>
      </Container>

      <Box component="section" sx={{textAlign: "center"}}>
        <Pjdb1Button
          label="クリア"
          variant="contained"
          color="inherit"
          isLoading={isLoading}
          sx={{m: 2}}
          onClick={clear}
        />
        <Pjdb1Button
          label="ワーク情報を登録"
          variant="contained"
          color="primary"
          isLoading={isLoading}
          sx={{m: 2}}
          onClick={() => {
            updateClass().catch(console.error)
          }}
        />
      </Box>
    </>
  );
}

function useFormHelper(params: EditClassFormProps) {
  const {makeMutation, ...helper} = useMutationHelper({
    onClear: () => {
      params.setInputs({
        ...editClassFormInitInputs,
        ...params.work.attr,
        [workAttrList.contentWeight.key]: params.work.attr[workAttrList.contentWeight.key]
          ? params.work.attr[workAttrList.contentWeight.key]!.toString()
          : undefined,
        [workAttrList.contentVolume.key]: params.work.attr[workAttrList.contentVolume.key]
          ? params.work.attr[workAttrList.contentVolume.key]!.toString()
          : undefined,
      });
    }
  });

  const updateClass = makeMutation({
    onCompleted: () => {
      if (params.onSuccess) {
        params.onSuccess();
      } else {
        helper.notification.savedMsg();
      }
    },
    onError: (err) => {
      helper.handleError({error: err, message: '更新処理が失敗しました。'});
      params.onError?.(err);
    },
    validate: () => true,
    callback: async () => {
      const instanceResult = await updateWorkClass({
        classId: params.work.attr[workAttrList.classId.key],
        ...params.inputs,
        [workAttrList.contentWeight.key]: params.inputs[workAttrList.contentWeight.key]
          ? Number(params.inputs[workAttrList.contentWeight.key])
          : undefined,
        [workAttrList.contentVolume.key]: params.inputs[workAttrList.contentVolume.key]
          ? Number(params.inputs[workAttrList.contentVolume.key])
          : undefined,
      });
      if (!instanceResult.isSuccess) {
        return helper.handleError({error: instanceResult.err, message: '更新処理が失敗しました。'});
      }

      return true;
    },
  });

  return {
    updateClass,
    ...helper,
  }
}
